<script>
   /*************************************************************/
   /* AppPlots component for Predict tab                        */
   /* -----------------------------------                       */
   /* defines plot components, controls and parameters          */
   /*************************************************************/

   import { untrack } from 'svelte';
   import { getDefaults } from 'svelte-controls-basic';
   import { Container, Button } from 'svelte-controls-basic';

   // global plot router
   import PlotsRouter from '@mdatools-apps/shared/plots/PlotsRouter.svelte';

   // import plot components
   import PlotAcceptance from '@mdatools-apps/shared/plots/PlotAcceptance.svelte';
   import PlotDistances from '@mdatools-apps/shared/plots/PlotDistances.svelte';
   import PlotScores from '@mdatools-apps/shared/plots/PlotScores.svelte';

   // import plot controls components
   import { getPlotAcceptanceControls, getPlotDistancesControls, getPlotScoresControls } from '@mdatools-apps/shared/plots/Controls.svelte.js';
   import { LINE_COLORS } from '@mdatools-apps/shared/plots/Defaults.js';

   let {
      model,
      simca,
      newset,
      newres,
      markOutlier,
      showContributions
   } = $props();

   // because most of the plot components expect JSON with results
   // assuming there is more than one result object we create it
   // same for the data
   const res = {'new': newres.pca};
   const data = {'new': newset};

   // this is derived as simca results can change without reloading the component
   const classres = $derived({'new': newres.simca});


   // JSON with plot components (scope is needed for providing correct properties)
   const plots = $derived({
      acceptance: {label: 'Acceptance', scope: 'objects', component: PlotAcceptance, buttons: buttons2},
      distances: {label: 'Distances', scope: 'objects', component: PlotDistances, buttons: buttons2},
      scores: {label: 'Scores', scope: 'objects', component: PlotScores, buttons: buttons1}
   })

   // JSON with plots controls (as Widget properties). the rules below are attached once and nothing
   // changes this object afterwards, so it is a plain one rather than '$state'. 'untrack' marks the
   // read of 'newres' as a deliberate capture of what is on screen at mount - the component is keyed
   // by the result, so a new result mounts a new one
   const controls = untrack(() => ({
      acceptance: getPlotAcceptanceControls({'new': newres.simca}),
      distances: getPlotDistancesControls(res),
      scores: getPlotScoresControls(res),
   }))

   // the factory marks these two 'hidden', which is where they start and not something the data
   // rules out - nothing here makes them impossible, they simply wait for the right selection.
   // 'hidden' is OR-ed with the rules below and would keep them hidden for good, so it is cleared
   // and their visibility is left entirely to 'hiddenWhen'
   controls.scores.colorByVar.hidden = false;
   controls.scores.confEllipse.hidden = false;

   // which controls do not apply to what the user has currently selected. the factories above set
   // 'hidden' for what the data rules out, and Widget combines the two with OR, so a rule here can
   // never bring back a control the data has excluded
   controls.scores.comp1.hiddenWhen       = (p) => p.plotType !== 'p';
   controls.scores.showEllipse.hiddenWhen = (p) => p.plotType !== 'p';
   controls.scores.colorBy.hiddenWhen     = (p) => p.plotType === 'l' || p.plotType === 'b';
   controls.scores.colorByVar.hiddenWhen  = (p) => p.colorBy !== 'variable';
   controls.scores.confEllipse.hiddenWhen = (p) => !(p.colorBy === 'groups' && p.plotType === 'p');

   // JSON with currently selected plots parameters
   let params = $state({
      acceptance: getDefaults(controls.acceptance),
      distances: getDefaults(controls.distances),
      scores: getDefaults(controls.scores),
   });

   // add additional fixed parameters for the acceptance plot
   $effect(() => {
      params.acceptance['ncomp'] = simca.ncomp;
      params.acceptance['title'] = `Acceptance (A = ${simca.ncomp})`;
      params.acceptance['limType'] = simca.limType;
      params.acceptance['alpha'] = simca.alpha;
      params.acceptance['gamma'] = simca.gamma;
   });

   $effect(() => {
      params.acceptance['colorByObj'] = {
         values: classres.new.v[simca.ncomp - 1].decs,
         classColmap: {in: LINE_COLORS.new, out: LINE_COLORS.new},
         labels: ["in", "out"],
         filled: classres.new.v[simca.ncomp - 1].decs.map(v => v === 'in'),
         showBar: false
      }
   })

   // parameters whose control has gone away are reset, because the plot reads them whatever is
   // shown: 'col1' is computed from 'colorBy' for every plot type, and 'getLim' widens the axes
   // from the stored ellipse ranges whenever 'showEllipse' or 'confEllipse' say so, neither of
   // them re-checking the plot type. the other hidden parameters are read behind the same
   // condition which hides them, so they can keep their value
   $effect(() => {
      const p = params.scores;
      if (controls.scores.showEllipse.hiddenWhen(p)) p.showEllipse = false;
      if (controls.scores.colorBy.hiddenWhen(p))     p.colorBy = 'none';
      if (controls.scores.confEllipse.hiddenWhen(p)) p.confEllipse = 'none';
   });

   // combine main elements to be shared with all plots together
   const props = $derived({res, classres, model, simca, data, ncomp: simca.ncomp});


</script>

{#snippet buttons1(sel)}
   <Container label="Selected">
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}

{#snippet buttons2(sel)}
   <Container label="Selected">
      <Button onclick={() => showContributions(sel)} text="plot" />
      <Button onclick={() => markOutlier(sel)} text="remove" class="destructive" />
   </Container>
{/snippet}

<PlotsRouter {plots} {controls} {props} bind:params={params} />